import type React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DELIVERY_FORMATS, type DeliveryFormat } from '@tekvoice/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import {
  ReadOnlyNotice,
  SETTINGS_QUERY_KEY,
  SettingSlider,
  useSettingsDraft,
} from '@/pages/settings/settings-form';

const FORMAT_LABELS: Record<DeliveryFormat, string> = {
  mp3: 'MP3 (no transcode)',
  wav_8000: 'WAV 8 kHz mono (PCM 16-bit)',
  ulaw_8000: 'µ-law 8 kHz mono (WAV container)',
};

const CACHE_CONTROL_RE = /^(public|private), max-age=(\d{1,7})$/;

function parseCacheControl(value: string): { visibility: 'public' | 'private'; maxAge: number } {
  const match = CACHE_CONTROL_RE.exec(value);
  if (!match) return { visibility: 'public', maxAge: 300 };
  return { visibility: match[1] as 'public' | 'private', maxAge: Number(match[2]) };
}

/** SPEC §8 — delivery format, optional ffmpeg processing, filename and cache-control. */
export function PublishingSettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { draft, setDraft, isLoading, readOnly, dirty } = useSettingsDraft('publishing');

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save');
      return api.settings.update({ publishing: draft });
    },
    onSuccess: async (result) => {
      setDraft(result.data.publishing);
      toast.success('Publishing settings saved');
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading || !draft) return <Skeleton className="h-64 w-full" />;

  const cacheControl = parseCacheControl(draft.cacheControl);
  const transcodes = draft.deliveryFormat !== 'mp3';

  return (
    <div className="space-y-4">
      <ReadOnlyNotice readOnly={readOnly} />
      <Card>
        <CardHeader>
          <CardTitle>Publishing</CardTitle>
          <CardDescription>
            Applied when a row is approved and its audio is copied to the published folder.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="delivery-format">Delivery format</Label>
            <Select
              value={draft.deliveryFormat}
              disabled={readOnly}
              onValueChange={(value) =>
                setDraft((previous) =>
                  previous ? { ...previous, deliveryFormat: value as DeliveryFormat } : previous,
                )
              }
            >
              <SelectTrigger id="delivery-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_FORMATS.map((format) => (
                  <SelectItem key={format} value={format}>
                    {FORMAT_LABELS[format]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {transcodes
                ? 'A .wav delivery file is produced next to the published .mp3.'
                : 'The published .mp3 is delivered as-is — no ffmpeg step.'}
            </p>
          </div>

          {transcodes ? (
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="process-audio">Process audio</Label>
                <p className="text-xs text-muted-foreground">
                  Trim leading/trailing silence and normalise loudness during the transcode.
                </p>
              </div>
              <Switch
                id="process-audio"
                checked={draft.processAudio}
                disabled={readOnly}
                onCheckedChange={(checked) =>
                  setDraft((previous) => (previous ? { ...previous, processAudio: checked } : previous))
                }
              />
            </div>
          ) : null}

          {transcodes && draft.processAudio ? (
            <>
              <SettingSlider
                id="loudness"
                label="Loudness target"
                value={draft.loudnessTargetLufs}
                min={-40}
                max={-5}
                step={1}
                disabled={readOnly}
                format={(value) => `${value} LUFS`}
                onChange={(value) =>
                  setDraft((previous) =>
                    previous ? { ...previous, loudnessTargetLufs: value } : previous,
                  )
                }
              />
              <SettingSlider
                id="silence"
                label="Silence threshold"
                value={draft.silenceThresholdDb}
                min={-90}
                max={-10}
                step={1}
                disabled={readOnly}
                format={(value) => `${value} dB`}
                onChange={(value) =>
                  setDraft((previous) =>
                    previous ? { ...previous, silenceThresholdDb: value } : previous,
                  )
                }
              />
            </>
          ) : null}

          <SettingSlider
            id="filename-words"
            label="Words in the filename"
            description="How many English words go into the frozen published slug."
            value={draft.filenameWordCount}
            min={1}
            max={10}
            step={1}
            disabled={readOnly}
            onChange={(value) =>
              setDraft((previous) => (previous ? { ...previous, filenameWordCount: value } : previous))
            }
          />

          <div className="space-y-3 rounded-md border p-3">
            <div className="space-y-2">
              <Label htmlFor="cache-visibility">Cache-Control</Label>
              <Select
                value={cacheControl.visibility}
                disabled={readOnly}
                onValueChange={(value) =>
                  setDraft((previous) =>
                    previous
                      ? { ...previous, cacheControl: `${value}, max-age=${cacheControl.maxAge}` }
                      : previous,
                  )
                }
              >
                <SelectTrigger id="cache-visibility" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">public</SelectItem>
                  <SelectItem value="private">private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <SettingSlider
              id="cache-max-age"
              label="max-age"
              value={cacheControl.maxAge}
              min={0}
              max={86_400}
              step={60}
              disabled={readOnly}
              format={(value) => `${value}s`}
              onChange={(value) =>
                setDraft((previous) =>
                  previous
                    ? { ...previous, cacheControl: `${cacheControl.visibility}, max-age=${value}` }
                    : previous,
                )
              }
            />
            <p className="text-xs text-muted-foreground">
              Sent with every upload: <code className="rounded bg-muted px-1">{draft.cacheControl}</code>
            </p>
          </div>

          {!readOnly ? (
            <div className="flex items-center gap-3">
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save publishing settings'}
              </Button>
              {dirty ? <span className="text-sm text-muted-foreground">Unsaved changes</span> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
